/**
 * Celebration effects after a correct answer: the broken layer bursting into
 * themed bits (or sparkles for a clean floor) and the "+340" floater. Driven
 * by engine-time progress (timeline.ts), so they freeze with the game when
 * paused. Not rendered at all under reduced motion.
 */
import { memo, useMemo } from 'react';
import type { BrickStyle } from '@dropstack/core';
import { BRICK_SPECS, burstParticles } from './bricks.ts';
import { TIMING } from './timeline.ts';

const LAYER_BITS = 14;
const FLOOR_SPARKLES = 12;

export interface BurstProps {
  kind: 'layer' | 'floor';
  brickStyle: BrickStyle;
  /** Seed (the problem's sequence number) so the same answer always bursts the same way. */
  seed: number;
  /** 0 → 1. */
  progress: number;
  /** Centre line of the burst, px from the top of the well. */
  y: number;
  /** Spread (px): how far bits fly sideways / up. */
  spread: number;
}

export const Burst = memo(function Burst({ kind, brickStyle, seed, progress, y, spread }: BurstProps) {
  const bits = useMemo(() => burstParticles(seed, kind === 'layer' ? LAYER_BITS : FLOOR_SPARKLES), [seed, kind]);
  const shape = kind === 'layer' ? BRICK_SPECS[brickStyle].particle : 'sparkle';
  const gravity = spread * 0.9;

  return (
    <div className={`burst burst--${kind}`} style={{ top: y }} aria-hidden="true">
      {bits.map((b, i) => {
        const p = Math.min(1, Math.max(0, progress - b.delay / TIMING.BURST_MS));
        const tx = b.dx * spread * p;
        const ty = b.dy * spread * 0.8 * p + gravity * p * p;
        const scale = b.scale * (1 - 0.45 * p);
        return (
          <span
            key={i}
            className={`burst__bit burst__bit--${shape} burst__bit--c${b.color}`}
            style={{
              left: `${b.x * 100}%`,
              opacity: 1 - p ** 3,
              transform: `translate(${tx.toFixed(1)}px, ${ty.toFixed(1)}px) rotate(${Math.round(b.rotate * p)}deg) scale(${scale.toFixed(3)})`,
            }}
          />
        );
      })}
    </div>
  );
});

export interface FloaterProps {
  text: string;
  /** Small line under the points ("Clean floor!"). */
  note?: string;
  /** 0 → 1. */
  progress: number;
  /** Where it starts, px from the top of the well. */
  y: number;
  reducedMotion: boolean;
}

/** "+340" rising from where the block landed, fading near the end. */
export function Floater({ text, note, progress, y, reducedMotion }: FloaterProps) {
  const rise = reducedMotion ? 0 : 64 * (1 - (1 - progress) ** 2);
  const opacity = progress < 0.65 ? 1 : Math.max(0, 1 - (progress - 0.65) / 0.35);
  const pop = reducedMotion || progress > 0.15 ? 1 : 0.6 + (progress / 0.15) * 0.4;
  return (
    <div className="floater" style={{ top: y, opacity, transform: `translate(-50%, ${(-rise).toFixed(1)}px) scale(${pop.toFixed(3)})` }} aria-hidden="true">
      <span className="floater__points">{text}</span>
      {note && <span className="floater__note">{note}</span>}
    </div>
  );
}
